/* eslint-disable @typescript-eslint/camelcase */
import Vue from 'vue';

declare const ET_Event: any;

const DOUBLE_CLICK_THRESHOLD = 500;
let lastClick = 0;

type EtrackerParams = {
	name: string;
	unique?: boolean;
	recursive?: boolean;
};

/**
 * Send a track of the click to the etracker API (loaded via Backend (ETrackerConfiguration)).
 * @param name Etracker name of the tracked element.
 */
function sendEtrackerClick(name: string) {
	if (typeof ET_Event !== 'undefined') {
		ET_Event.click(name, 'cta');
	}
}

/**
 * In order to correctly distinguish tracked elements,
 * every etracker should have a unique name. In order to guarantee this,
 * two elements that are named similiarly, are provided with a trailing number each.
 * This is only valid for "normal" tracked elements!
 * Unique tracked elements are <strong>not</strong> provided with a trailing number.
 * Instead the normal tracked element, that has the same name as the unique one,
 * gets a trailing number.
 * @param name etracker name of the currently processed element
 */
function handleSimilarNamedEtrackers(element: HTMLElement) {
	const etrackerName = element.dataset.etrack;
	const sameUniqueNames = document.querySelectorAll('[data-etrack-unique="' + etrackerName + '"]');
	const sameNames = document.querySelectorAll('[data-etrack="' + etrackerName + '"]');
	if (sameUniqueNames.length || sameNames.length > 1) {
		sameNames.forEach((element: Element, i: number) => {
			(element as HTMLElement).dataset.etrack = etrackerName + '_' + (i + 1);
		});
	}
}

/**
 * We only want to track one click,
 * even if the user performed a double click on a tracked element.
 * @returns false if it was a double click
 * (the second click was within a certain threshold), true otherwise
 */
function lastClickAboveThreshold() {
	const now = Date.now();
	// don't track the second click of a double click
	if (now - lastClick <= DOUBLE_CLICK_THRESHOLD) {
		return false;
	}
	lastClick = now;
	return true;
}

/**
 * Click listener for tracked elements.
 * @param element The element to be tracked
 * @param unique true, if this element has a unique etracker name
 */
function etrackerClickEvent(element: HTMLElement, unique?: boolean) {
	if (!lastClickAboveThreshold()) return;
	let etrackerName;
	if (unique) {
		etrackerName = element.dataset.etrackUnique;
	} else {
		handleSimilarNamedEtrackers(element);
		etrackerName = element.dataset.etrack;
	}
	etrackerName && sendEtrackerClick(etrackerName);
}

/**
 * When the tracking should be done recursively, all `a` and `button` tags
 * within the designated element are registered for tracking.
 * @param element The element, whose trackable child nodes should be tracked
 * @param name The etracker name of the trackable child nodes
 */
function spreadEtrackerToChildNodes(element: HTMLElement, name: string) {
	element.querySelectorAll('a:not([data-etrack]), button:not([data-etrack])').forEach((childEl: Element) => {
		(childEl as HTMLElement).dataset.etrack = name;
		childEl.addEventListener('mousedown', () => etrackerClickEvent(childEl as HTMLElement));
	});
}

/**
 * Directive to handle all etracker related code.
 * @param name: The etracker name of the element. When tracking a click, this name is sent to the etracker API.
 * @param unique: If true, the etracker name is a unique one. All "normal" etracker elements, that are similarly named, receive a trailing number.
 * @param recursive: If true, the element, on which the directive is set, isn't tracked itself but all trackable (`a`s and `button`s) children will be.
 */
export default Vue.directive('etracker', {
	bind: function(el: HTMLElement, { value }: { value?: EtrackerParams }) {
		if (value) {
			// set etrack data on uniquely named element
			if (value.unique) {
				el.dataset.etrackUnique = value.name;
			} else {
				// if the tracking should be done recursively,
				// we only have to track trackable child elements and not the element itself
				if (value.recursive) {
					spreadEtrackerToChildNodes(el, value.name);
				} else {
					el.dataset.etrack = value.name;
				}
			}

			!value.recursive && el.addEventListener('mousedown', () => etrackerClickEvent(el, value?.unique));
		}
	},
});
